import type { ChipColor } from 'common/components';
import type { ManagerAgentAnalyticsItem, ManagerAgentAnalyticsStatus } from 'services/api/getManagerAgentAnalytics';
import { formatMillisecondsAsDayHourMinSec } from 'utils/dateformat.utils';

export type MetricKind = 'count' | 'rate' | 'duration';

export type PerformanceMetricSortKey =
    | 'transfers'
    | 'transfer-response'
    | 'transfer-conversion'
    | 'callbacks'
    | 'callback-response'
    | 'callback-conversion'
    | 'active-time'
    | 'waiting';

export type SortDirection = 'asc' | 'desc';

const noData = '—';

function formatToInteger(value: number): string | number {
    return Number.isInteger(value) ? value : value.toFixed(2);
}

export function formatMetricValue(value: number | null | undefined, kind: MetricKind): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return noData;
    }
    if (kind === 'rate') {
        return `${formatToInteger(value * 100)}%`;
    }
    if (kind === 'duration') {
        if (value <= 0) {
            return noData;
        }
        return formatMillisecondsAsDayHourMinSec(value * 1000);
    }
    return value.toString();
}

export function formatAgentStatus(status: ManagerAgentAnalyticsStatus): string {
    switch (status) {
        case 'online':
            return 'Online';
        case 'oncall':
            return 'On call';
        case 'offline':
            return 'Offline';
        default:
            return status;
    }
}

export function agentStatusChipColor(status: ManagerAgentAnalyticsStatus): ChipColor {
    switch (status) {
        case 'online':
            return 'success';
        case 'oncall':
            return 'warning';
        case 'offline':
        default:
            return 'secondary';
    }
}

export function getTransferredCallsTotal(agents: Array<Pick<ManagerAgentAnalyticsItem, 'agentPerformance'>>): number {
    return agents.reduce((sum, agent) => sum + (agent.agentPerformance.transfersCompletedToday ?? 0), 0);
}

export function getOnlineAgentsCount(agents: Array<Pick<ManagerAgentAnalyticsItem, 'status'>>): number {
    return agents.filter((agent) => agent.status === 'online' || agent.status === 'oncall').length;
}

export function getOnlineAgentsStatusBreakdown(agents: Array<Pick<ManagerAgentAnalyticsItem, 'status'>>): {
    available: number;
    onCall: number;
} {
    return agents.reduce(
        (acc, agent) => {
            if (agent.status === 'online') acc.available += 1;
            if (agent.status === 'oncall') acc.onCall += 1;
            return acc;
        },
        { available: 0, onCall: 0 }
    );
}

export function getAgentsWithCallsCount(agents: Array<Pick<ManagerAgentAnalyticsItem, 'agentPerformance'>>): number {
    return agents.filter((agent) => (agent.agentPerformance.transfersCompletedToday ?? 0) > 0).length;
}

export function getMetricSortValue(row: ManagerAgentAnalyticsItem, key: PerformanceMetricSortKey): number | null {
    switch (key) {
        case 'transfers':
            return row.agentPerformance.transfersCompletedToday ?? null;
        case 'transfer-response':
            return row.agentPerformance.transferResponseRate?.value ?? null;
        case 'transfer-conversion':
            return row.agentPerformance.transferConversionRate?.value ?? null;
        case 'callbacks':
            return row.agentPerformance.callbacksHandled ?? null;
        case 'callback-response':
            return row.agentPerformance.callbackResponseRate?.value ?? null;
        case 'callback-conversion':
            return row.agentPerformance.callbackConversionRate?.value ?? null;
        case 'active-time':
            return row.operational.timeOnlineTodaySeconds ?? null;
        case 'waiting':
            return row.operational.waitingTimeSeconds ?? null;
        default:
            return null;
    }
}

export function compareMetricSortValues(a: number | null, b: number | null, direction: SortDirection): number {
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return direction === 'asc' ? a - b : b - a;
}
